import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Download, Loader2, Ticket, Mail, Users } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

export default function Rsvps() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/rsvps").then((r) => setItems(r.data)).catch(() => setItems([])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const remove = async (r) => {
    if (!window.confirm("Delete this RSVP?")) return;
    try { await api.delete(`/rsvps/${r.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const exportCsv = () => {
    const rows = [["event_title", "name", "email", "guests", "created_at"]];
    items.forEach((r) => rows.push([r.event_title, r.name, r.email, r.guests, r.created_at]));
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = "rsvps.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const totalGuests = items.reduce((s, r) => s + (Number(r.guests) || 1), 0);

  return (
    <div data-testid="admin-rsvps">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display font-black uppercase text-3xl">RSVPs</h1>
          <p className="mt-1 text-muted-foreground text-sm">{items.length} RSVP{items.length === 1 ? "" : "s"} · {totalGuests} people expected</p>
        </div>
        <button onClick={exportCsv} className="inline-flex items-center gap-2 rounded-full border border-border bg-card font-bold px-5 py-2.5 hover:border-[#2CACD4] transition-colors cursor-pointer" data-testid="rsvps-export">
          <Download size={17} /> Export CSV
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#2CACD4]" /></div>
      ) : items.length === 0 ? (
        <div className="mt-8 rounded-2xl border border-border bg-card p-12 text-center text-muted-foreground"><Ticket className="mx-auto mb-3" /> No RSVPs yet.</div>
      ) : (
        <div className="mt-6 overflow-x-auto rounded-2xl border border-border bg-card">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3 font-bold">Event</th>
                <th className="px-4 py-3 font-bold">Name</th>
                <th className="px-4 py-3 font-bold">Email</th>
                <th className="px-4 py-3 font-bold">Guests</th>
                <th className="px-4 py-3 text-right font-bold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {items.map((r) => (
                <tr key={r.id} className="hover:bg-muted/40" data-testid="rsvp-row">
                  <td className="px-4 py-3 font-semibold">{r.event_title || "—"}</td>
                  <td className="px-4 py-3">{r.name}</td>
                  <td className="px-4 py-3 text-muted-foreground"><span className="inline-flex items-center gap-1.5"><Mail size={13} /> {r.email}</span></td>
                  <td className="px-4 py-3"><span className="inline-flex items-center gap-1.5"><Users size={13} /> {r.guests || 1}</span></td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => remove(r)} className="rounded-lg p-2 text-[#DB1A5C] hover:bg-[#DB1A5C]/10 cursor-pointer" aria-label="Delete" data-testid="rsvp-delete"><Trash2 size={15} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
